import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../api/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faPen, faTrash, faSearch, faShieldAlt,
    faUsers, faUser, faTimes, faEye, faEyeSlash, faSyncAlt,
    faBuilding, faSortAmountDown, faSortAmountUp
} from '@fortawesome/free-solid-svg-icons';
import { useAuth, ROLES } from '../context/AuthContext';

const ROLE_OPTIONS = [
    { value: 99, label: 'Superadmin', desc: 'Platform owner, full access across all organizations' },
    { value: 98, label: 'Org Admin', desc: 'Organization Administrator, manages own org users and data' },
    { value: 97, label: 'Manager', desc: 'Own + team CRUD access within organization' },
    { value: 96, label: 'Employee', desc: 'Create, Read, Update on own records only' },
];

const ROLE_BADGE_STYLE = {
    99: { label: 'Superadmin' },
    98: { label: 'Org Admin' },
    97: { label: 'Manager' },
    96: { label: 'Employee' },
};

const EMPTY_FORM = {
    name: '',
    email: '',
    password: '',
    designation: '',
    RoleID: 96,
    OrgID: '',
    ReportingTo: '',
    department: ''
};

function Users() {
    const { currentUser, isSuper, isAdmin, activeOrgId } = useAuth();
    const [users, setUsers] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters & Pagination
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('ALL');
    const [orgFilter, setOrgFilter] = useState('ALL');
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [entries, setEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Modals
    const [showModal, setShowModal] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [error, setError] = useState('');

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/users');
            setUsers(res.data || []);
        } catch (err) {
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchOrgs = useCallback(async () => {
        if (!isSuper) return;
        try {
            const res = await api.get('/organizations');
            setOrganizations(res.data || []);
        } catch (err) {
            console.error('Error fetching organizations:', err);
        }
    }, [isSuper]);

    useEffect(() => {
        fetchUsers();
        fetchOrgs();
    }, [fetchUsers, fetchOrgs]);

    // Re-fetch users if active organization in header changes
    useEffect(() => {
        const handleOrgChange = () => fetchUsers();
        window.addEventListener('sv_active_org_changed', handleOrgChange);
        return () => window.removeEventListener('sv_active_org_changed', handleOrgChange);
    }, [fetchUsers]);

    const openCreate = () => {
        setEditUser(null);
        setForm({
            ...EMPTY_FORM,
            OrgID: isSuper ? (activeOrgId && activeOrgId !== 'all' ? activeOrgId : (organizations[0]?.OrgID || '1')) : (currentUser?.OrgID || '1'),
            RoleID: 96
        });
        setShowPassword(false);
        setError('');
        setShowModal(true);
    };

    const openEdit = (user) => {
        setEditUser(user);
        setForm({
            name: user.name || '',
            email: user.email || '',
            password: '',
            designation: user.designation || '',
            RoleID: user.RoleID || 96,
            OrgID: user.OrgID ? String(user.OrgID) : '',
            ReportingTo: user.ReportingTo || '',
            department: user.department || ''
        });
        setShowPassword(false);
        setError('');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditUser(null);
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.name.trim()) { setError('Name is required'); return; }
        if (!form.email.trim()) { setError('Email is required'); return; }
        if (!editUser && !form.password) { setError('Password is required for new users'); return; }

        setSubmitting(true);
        try {
            const targetRole = Number(form.RoleID);
            const payload = {
                name: form.name.trim(),
                email: form.email.trim(),
                designation: form.designation,
                RoleID: targetRole,
                ReportingTo: form.ReportingTo ? Number(form.ReportingTo) : null,
                department: form.department
            };

            if (isSuper) {
                payload.OrgID = targetRole === ROLES.SUPERADMIN ? null : (form.OrgID ? Number(form.OrgID) : 1);
            }

            if (form.password) payload.password = form.password;

            if (editUser) {
                await api.put(`/users/${editUser.empId}`, payload);
            } else {
                await api.post('/users', payload);
            }

            await fetchUsers();
            closeModal();
        } catch (err) {
            setError(err.response?.data?.error || 'Operation failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/users/${deleteTarget.empId}`);
            setDeleteTarget(null);
            await fetchUsers();
        } catch (err) {
            alert(err.response?.data?.error || 'Delete failed');
        }
    };

    // Filtered Users List
    const filtered = useMemo(() => {
        return users.filter(u => {
            const term = search.toLowerCase().trim();
            const matchesSearch = !term ||
                u.name?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term) ||
                u.designation?.toLowerCase().includes(term) ||
                u.OrgName?.toLowerCase().includes(term) ||
                u.department?.toLowerCase().includes(term) ||
                String(u.empId).includes(term);

            const matchesRole = roleFilter === 'ALL' || String(u.RoleID) === String(roleFilter);

            const matchesOrg = orgFilter === 'ALL' ||
                (orgFilter === 'NONE' && !u.OrgID) ||
                String(u.OrgID) === String(orgFilter);

            return matchesSearch && matchesRole && matchesOrg;
        });
    }, [users, search, roleFilter, orgFilter]);

    // Sorted Users List
    const sortedUsers = useMemo(() => {
        const list = [...filtered];
        list.sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            if (sortField === 'RoleID') {
                valA = Number(a.RoleID) || 0;
                valB = Number(b.RoleID) || 0;
            } else if (sortField === 'empId') {
                valA = Number(a.empId) || 0;
                valB = Number(b.empId) || 0;
            } else if (typeof valA === 'string') {
                valA = (valA || '').toLowerCase();
                valB = (valB || '').toLowerCase();
            }

            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        return list;
    }, [filtered, sortField, sortOrder]);

    // Pagination
    const totalPages = Math.ceil(sortedUsers.length / entries);
    const paginatedUsers = useMemo(() => {
        const start = (currentPage - 1) * entries;
        return sortedUsers.slice(start, start + entries);
    }, [sortedUsers, currentPage, entries]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    // Managers for "Reports To" list
    const getManagerOptions = (selectedOrgId) => {
        return users.filter(u => {
            const isManagerOrAdmin = Number(u.RoleID) >= ROLES.MANAGER;
            if (!isManagerOrAdmin) return false;
            if (Number(u.RoleID) === ROLES.SUPERADMIN) return true;
            if (selectedOrgId) {
                return String(u.OrgID) === String(selectedOrgId);
            }
            return true;
        });
    };

    const availableRoles = isSuper ? ROLE_OPTIONS : ROLE_OPTIONS.filter(r => r.value !== ROLES.SUPERADMIN);

    // KPI Metrics
    const totalUsersCount = users.length;
    const managersAndAdminsCount = users.filter(u => Number(u.RoleID) >= 97).length;
    const repsCount = users.filter(u => Number(u.RoleID) === 96).length;
    const distinctOrgsCount = new Set(users.map(u => u.OrgID).filter(Boolean)).size;

    const isFiltered = Boolean(
        search.trim() ||
        roleFilter !== 'ALL' ||
        orgFilter !== 'ALL'
    );

    const handleResetFilters = () => {
        setSearch('');
        setRoleFilter('ALL');
        setOrgFilter('ALL');
        setCurrentPage(1);
    };

    if (!isAdmin) {
        return (
            <Layout>
                <div className="dome-dashboard-wrap">
                    <div className="dome-table-card p-40 text-center">
                        <div className="text-32 text-danger mb-12">
                            <FontAwesomeIcon icon={faShieldAlt} />
                        </div>
                        <h2 className="font-18 text-dark font-600 mb-6">Access Restricted</h2>
                        <p className="text-muted font-13 mb-16">
                            Only Administrators have permission to access the User Management directory.
                        </p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="dome-dashboard-wrap">

                {/* ================= 1. TOP HEADER ================= */}
                <div className="dome-dash-header-row">
                    <div className="dome-dash-title-wrap">
                        <h1 className="dome-dash-h1">User Management</h1>
                    </div>

                    <div className="dome-dash-actions">
                        <button
                            type="button"
                            className="btn-dome-secondary"
                            onClick={fetchUsers}
                            title="Refresh users directory"
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                            <span>Refresh</span>
                        </button>

                        <button
                            type="button"
                            className="btn-dome-new"
                            onClick={openCreate}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <span>Add New User</span>
                        </button>
                    </div>
                </div>

                {/* ================= 2. 4-CARD KPI RIBBON ================= */}
                <div className="dome-kpi-strip">
                    {/* Card 1: Total Users */}
                    <div
                        className={`dome-kpi-card cursor-pointer ${roleFilter === 'ALL' && !isFiltered ? 'active-filter-card' : ''}`}
                        onClick={handleResetFilters}
                        title="View All Users"
                    >
                        <span className="dome-kpi-label">Total Users</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{totalUsersCount}</span>
                        </div>
                        <span className="dome-kpi-sub">Registered platform accounts</span>
                    </div>

                    {/* Card 2: Leaders & Managers */}
                    <div
                        className={`dome-kpi-card cursor-pointer ${roleFilter === '97' ? 'active-filter-card' : ''}`}
                        onClick={() => {
                            setRoleFilter(prev => prev === '97' ? 'ALL' : '97');
                            setCurrentPage(1);
                        }}
                        title="Filter Managers & Admins"
                    >
                        <span className="dome-kpi-label">Managers & Admins</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num text-primary">{managersAndAdminsCount}</span>
                        </div>
                        <span className="dome-kpi-sub">Supervisory and admin roles</span>
                    </div>

                    {/* Card 3: Sales Representatives */}
                    <div
                        className={`dome-kpi-card cursor-pointer ${roleFilter === '96' ? 'active-filter-card' : ''}`}
                        onClick={() => {
                            setRoleFilter(prev => prev === '96' ? 'ALL' : '96');
                            setCurrentPage(1);
                        }}
                        title="Filter Sales Representatives"
                    >
                        <span className="dome-kpi-label">Representatives</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num text-success">{repsCount}</span>
                        </div>
                        <span className="dome-kpi-sub">Field agents & sales reps</span>
                    </div>

                    {/* Card 4: Tenant Organizations */}
                    <div className="dome-kpi-card">
                        <span className="dome-kpi-label">Organizations</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{distinctOrgsCount || 1}</span>
                        </div>
                        <span className="dome-kpi-sub">Active tenant domains</span>
                    </div>
                </div>

                {/* ================= 3. FILTER TOOLBAR (2-ROW SYSTEM) ================= */}
                <div className="dome-dash-subnav-strip dome-opp-subnav">
                    {/* Row 1: Full-Width Search Box */}
                    <div className="dome-opp-search-row">
                        <div className="dome-search-full-box">
                            <FontAwesomeIcon icon={faSearch} className="dome-search-full-icon" />
                            <input
                                type="text"
                                placeholder="Search users by name, email, designation, organization, department..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="dome-search-full-input"
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="dome-search-clear-mini"
                                    onClick={() => { setSearch(''); setCurrentPage(1); }}
                                    title="Clear search"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Row 2: Stacked Filters */}
                    <div className="dome-opp-filters-row">
                        {/* Role Filter */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">Access Role</label>
                            <select
                                value={roleFilter}
                                onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-select"
                            >
                                <option value="ALL">All Roles ({users.length})</option>
                                {availableRoles.map(r => (
                                    <option key={r.value} value={String(r.value)}>
                                        {r.label} ({users.filter(u => Number(u.RoleID) === Number(r.value)).length})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Organization Filter (Superadmin only) */}
                        {isSuper && organizations.length > 0 && (
                            <div className="dome-stacked-filter-item">
                                <label className="dome-stacked-filter-label">Organization</label>
                                <select
                                    value={orgFilter}
                                    onChange={(e) => { setOrgFilter(e.target.value); setCurrentPage(1); }}
                                    className="dome-stacked-select"
                                >
                                    <option value="ALL">All Organizations</option>
                                    {organizations.map(o => (
                                        <option key={o.OrgID} value={String(o.OrgID)}>
                                            {o.OrgName} ({users.filter(u => String(u.OrgID) === String(o.OrgID)).length})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Reset Filters Button */}
                        {isFiltered && (
                            <div className="dome-stacked-filter-item dome-stacked-reset-item">
                                <label className="dome-stacked-filter-label">&nbsp;</label>
                                <button
                                    type="button"
                                    className="dome-clear-filters-mini"
                                    onClick={handleResetFilters}
                                    title="Reset all filters"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="mr-4px" />
                                    Clear
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ================= 4. MAIN USERS TABLE CARD ================= */}
                <div className="dome-dash-card">
                    <div className="dome-dash-card-header">
                        <h3 className="dome-dash-card-title">User Accounts Directory</h3>
                        <div className="dome-table-header-controls">
                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Sort:</span>
                                <select
                                    value={sortField}
                                    onChange={(e) => setSortField(e.target.value)}
                                    className="dome-mini-select"
                                >
                                    <option value="empId">ID</option>
                                    <option value="name">User Name</option>
                                    <option value="RoleID">Access Role</option>
                                    <option value="department">Department</option>
                                </select>
                                <button
                                    type="button"
                                    className="dome-sort-dir-mini"
                                    onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                                    title={`Sort Order: ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
                                >
                                    <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountDown : faSortAmountUp} />
                                </button>
                            </div>

                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Show:</span>
                                <select
                                    value={entries}
                                    onChange={(e) => {
                                        setEntries(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="dome-mini-select"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="p-20 text-center text-muted font-13">
                            <FontAwesomeIcon icon={faSyncAlt} spin className="mr-6px text-primary" />
                            Loading team members directory...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-20 text-center">
                            <p className="text-muted text-12 mb-16">
                                {isFiltered ? "No team members match your current filters." : "Create your first user account to get started."}
                            </p>
                            {isFiltered ? (
                                <button
                                    type="button"
                                    className="btn-dome-secondary"
                                    onClick={handleResetFilters}
                                >
                                    Reset Filters
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn-dome-new"
                                    onClick={openCreate}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    <span>Add User</span>
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="dome-dash-table-wrap">
                            <table className="dome-dash-table dome-users-table">
                                <thead>
                                    <tr>
                                        <th className="th-id cursor-pointer" onClick={() => handleSort('empId')}>
                                            ID {sortField === 'empId' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>
                                        <th onClick={() => handleSort('name')} className="cursor-pointer">
                                            User {sortField === 'name' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>
                                        <th>Organization</th>
                                        <th>Designation</th>
                                        <th onClick={() => handleSort('RoleID')} className="cursor-pointer">
                                            Role {sortField === 'RoleID' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>
                                        <th>Reports To</th>
                                        <th>Department</th>
                                        <th className="text-right" style={{ width: '80px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedUsers.map(user => {
                                        const roleMeta = ROLE_BADGE_STYLE[user.RoleID] || { label: 'Unknown' };
                                        const isSelf = user.empId === currentUser.empId;

                                        return (
                                            <tr key={user.empId}>
                                                {/* ID */}
                                                <td>
                                                    <span className="dome-id-pill">{user.empId}</span>
                                                </td>

                                                {/* User Info */}
                                                <td>
                                                    <div className="d-flex align-center gap-10">
                                                        <div className="dome-lead-name-col">
                                                            <span className="dome-lead-name-link font-600">
                                                                {user.name} {isSelf && <span className="self-tag ml-6">You</span>}
                                                            </span>
                                                            <span className="text-muted font-11">{user.email}</span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Organization */}
                                                <td>
                                                    {user.RoleID === ROLES.SUPERADMIN ? (
                                                        <span className="text-muted font-12">Global Superadmin</span>
                                                    ) : (
                                                        <div>
                                                            <div>
                                                                {user.OrgCode && (
                                                                    <span className="text-muted font-11">({user.OrgCode}) </span>
                                                                )}
                                                                {user.OrgName || 'CodexterLabs'}
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>

                                                {/* Designation */}
                                                <td>{user.designation || '—'}</td>

                                                {/* Role */}
                                                <td>
                                                    <span>
                                                        {roleMeta.label}
                                                    </span>
                                                </td>

                                                {/* Reports To */}
                                                <td>{user.ManagerName || '—'}</td>

                                                {/* Department */}
                                                <td>{user.department || '—'}</td>

                                                {/* Actions */}
                                                <td>
                                                    <div className="dome-row-actions" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            type="button"
                                                            className="dome-icon-btn"
                                                            onClick={() => openEdit(user)}
                                                            title="Edit user details"
                                                        >
                                                            <FontAwesomeIcon icon={faPen} />
                                                        </button>
                                                        {!isSelf && (
                                                            <button
                                                                type="button"
                                                                className="dome-icon-btn dome-icon-btn-danger"
                                                                onClick={() => setDeleteTarget(user)}
                                                                title="Delete user"
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ================= 5. TABLE FOOTER & PAGINATION ================= */}
                    <div className="dome-table-footer">
                        <span className="dome-table-counter">
                            Showing {sortedUsers.length === 0 ? 0 : (currentPage - 1) * entries + 1}&ndash;{Math.min(currentPage * entries, sortedUsers.length)} of {sortedUsers.length} entries
                            {isFiltered && ` (filtered from ${users.length} total)`}
                        </span>

                        <div className="dome-pagination-controls">
                            <button
                                type="button"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                                className="dome-page-btn"
                            >
                                Prev
                            </button>

                            <span className="dome-page-indicator">
                                Page {currentPage} of {totalPages || 1}
                            </span>

                            <button
                                type="button"
                                disabled={currentPage === totalPages || totalPages === 0}
                                onClick={() => setCurrentPage(p => p + 1)}
                                className="dome-page-btn"
                            >
                                Next
                            </button>
                        </div>
                    </div>

                </div>

                {/* ================= ADD / EDIT USER MODAL ================= */}
                {showModal && (
                    <div className="modal-overlay" onClick={closeModal}>
                        <div className="modal dome-modal-card" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header modal-header-flex">
                                <h3>{editUser ? `Edit User: ${editUser.name}` : 'Add New Team Member'}</h3>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="modal-btn-close"
                                    title="Close Modal"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="d-flex flex-col flex-1 overflow-hidden">
                                <div className="modal-body">
                                    {error && (
                                        <div className="modal-error-alert mb-12">{error}</div>
                                    )}

                                    <div className="form-grid">
                                        {/* Full Name */}
                                        <div className="input-group">
                                            <label>Full Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                required
                                                placeholder="e.g. Alex Mercer"
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            />
                                        </div>

                                        {/* Email */}
                                        <div className="input-group">
                                            <label>Email Address <span className="text-danger">*</span></label>
                                            <input
                                                type="email"
                                                required
                                                placeholder="alex@organization.com"
                                                value={form.email}
                                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            />
                                        </div>

                                        {/* Password */}
                                        <div className="input-group">
                                            <label>
                                                Password{' '}
                                                {editUser ? (
                                                    <span className="text-muted">(leave blank to keep current)</span>
                                                ) : (
                                                    <span className="text-danger">*</span>
                                                )}
                                            </label>

                                            <div className="pos-relative password-field">
                                                <input
                                                    type={showPassword ? 'text' : 'password'}
                                                    placeholder={editUser ? '••••••••' : 'Min 6 characters'}
                                                    value={form.password}
                                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                                    required={!editUser}
                                                />
                                            </div>
                                        </div>

                                        {/* Designation */}
                                        <div className="input-group">
                                            <label>Designation</label>
                                            <input
                                                type="text"
                                                placeholder="e.g. Senior Account Executive"
                                                value={form.designation}
                                                onChange={(e) => setForm({ ...form, designation: e.target.value })}
                                            />
                                        </div>

                                        {/* Role Assignment */}
                                        <div className="input-group">
                                            <label>Role Assignment <span className="text-danger">*</span></label>
                                            <select
                                                value={form.RoleID}
                                                onChange={(e) => setForm({ ...form, RoleID: Number(e.target.value) })}
                                            >
                                                {availableRoles.map(r => (
                                                    <option key={r.value} value={r.value}>
                                                        {r.label} (Role {r.value})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Organization (Superadmin only) */}
                                        {isSuper && Number(form.RoleID) !== ROLES.SUPERADMIN && (
                                            <div className="input-group">
                                                <label>Assigned Organization <span className="text-danger">*</span></label>
                                                <select
                                                    value={form.OrgID}
                                                    onChange={(e) => setForm({ ...form, OrgID: e.target.value, ReportingTo: '' })}
                                                    required
                                                >
                                                    <option value="">-- Select Organization --</option>
                                                    {organizations.map(o => (
                                                        <option key={o.OrgID} value={String(o.OrgID)}>
                                                            {o.OrgName} ({o.OrgCode})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Reports To (Manager) */}
                                        {Number(form.RoleID) !== ROLES.SUPERADMIN && (
                                            <div className="input-group">
                                                <label>Reports To (Manager)</label>
                                                <select
                                                    value={form.ReportingTo}
                                                    onChange={(e) => setForm({ ...form, ReportingTo: e.target.value })}
                                                >
                                                    <option value="">-- No Direct Manager --</option>
                                                    {getManagerOptions(form.OrgID)
                                                        .filter(m => !editUser || m.empId !== editUser.empId)
                                                        .map(m => (
                                                            <option key={m.empId} value={m.empId}>
                                                                {m.name} ({ROLE_BADGE_STYLE[m.RoleID]?.label || 'Manager'})
                                                            </option>
                                                        ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Department */}
                                        <div className="input-group">
                                            <label>Department</label>
                                            <input
                                                type="text"
                                                placeholder="e.g. Enterprise Sales"
                                                value={form.department}
                                                onChange={(e) => setForm({ ...form, department: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn-dome-secondary"
                                        onClick={closeModal}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-dome-new"
                                        disabled={submitting}
                                    >
                                        {submitting ? 'Saving...' : editUser ? 'Save Changes' : 'Create User'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ================= DELETE CONFIRMATION MODAL ================= */}
                {deleteTarget && (
                    <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
                        <div className="modal modal-confirm-sm" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Confirm Delete User</h3>
                            </div>
                            <div className="modal-body">
                                <p className="confirm-modal-text">
                                    Are you sure you want to delete user account <strong>{deleteTarget.name}</strong> ({deleteTarget.email})?
                                    This action cannot be undone.
                                </p>
                                <div className="modal-footer mt-20">
                                    <button
                                        type="button"
                                        className="btn-dome-secondary"
                                        onClick={() => setDeleteTarget(null)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-dome-new btn-dome-submit"
                                        onClick={handleDelete}
                                        style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                                    >
                                        Delete User
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </Layout>
    );
}

export default Users;
